"use client"

import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { buildIntentsExplorerTxUrl } from "@src/components/DefuseSDK/constants/blockchains"
import type { IntentDescription } from "@src/components/DefuseSDK/features/machines/swapIntentMachine"
import { isSamePaymentFamily } from "@src/components/DefuseSDK/features/withdraw/utils/paymentTokenSelection"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import { WithdrawStatus } from "@src/components/WithdrawStatus"
import { trackAndRefreshFeed } from "@src/features/activity/lib/unifiedFeedAPI"
import {
  type RegisterWithdrawParams,
  type TrackedWithdrawRef,
  withdrawTrackerMachine,
} from "@src/machines/withdrawTrackerMachine"
import { useSelector } from "@xstate/react"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { createActor } from "xstate"
import { useActivityDock } from "./ActivityDockProvider"

export type TrackedWithdrawIntent = TrackedWithdrawRef

type WithdrawTrackerMachineContextType = {
  trackedWithdraws: TrackedWithdrawIntent[]
  registerWithdraw: (params: RegisterWithdrawParams) => void
  dismissWithdraw: (id: string) => void
  markWithdrawSeen: (id: string) => void
  hasActiveWithdraw: (id: string) => boolean
}

const WithdrawTrackerMachineContext = createContext<
  WithdrawTrackerMachineContextType | undefined
>(undefined)

export function WithdrawTrackerMachineProvider({
  children,
}: { children: ReactNode }) {
  const [actorRef] = useState(() => createActor(withdrawTrackerMachine).start())
  const { addDockItem, removeDockItem, settleDockItem } = useActivityDock()

  const trackedWithdraws = useSelector(actorRef, (s) => s.context.withdrawRefs)

  useEffect(() => {
    const sub = actorRef.on("ONE_CLICK_SETTLED", (event) => {
      const settledWithdraw = actorRef
        .getSnapshot()
        .context.withdrawRefs.find(
          (withdraw) => withdraw.depositAddress === event.data.depositAddress
        )
      if (!settledWithdraw) return

      settleDockItem(`withdraw-${settledWithdraw.id}`)

      if (event.data.status === "SUCCESS") {
        // Cross-asset only: omit referenceId so the swap row stays visible.
        // Same-family swaps are deduped by /api/activities against the withdrawal.
        // Compare paymentToken vs tokenOut — tokenIn is just a view of tokenOut.
        const isCrossAsset = !isSamePaymentFamily(
          settledWithdraw.paymentToken,
          settledWithdraw.tokenOut
        )
        trackAndRefreshFeed({
          type: "withdrawal_success",
          metadata: {
            token: settledWithdraw.tokenOut.symbol,
            amount: formatTokenValue(
              settledWithdraw.amountOut.amount,
              settledWithdraw.amountOut.decimals
            ),
            chain: settledWithdraw.chainName,
            recipient: settledWithdraw.recipient,
            isConfidential: settledWithdraw.isConfidential || undefined,
          },
          ...(isCrossAsset
            ? {}
            : { referenceId: settledWithdraw.depositAddress }),
        })
      }
    })
    return () => sub.unsubscribe()
  }, [actorRef, settleDockItem])

  const registerWithdraw = useCallback(
    (params: RegisterWithdrawParams) => {
      if (params.intentDescription.type !== "withdraw") return

      const id = params.intentHash

      actorRef.send({ type: "REGISTER_WITHDRAW", params })

      const withdraw = actorRef
        .getSnapshot()
        .context.withdrawRefs.find((w) => w.id === id)
      if (!withdraw) return

      const withdrawDescription = params.intentDescription as Extract<
        IntentDescription,
        { type: "withdraw" }
      >
      const formattedAmount = formatTokenValue(
        withdrawDescription.totalAmountOut.amount,
        withdrawDescription.totalAmountOut.decimals,
        { min: 0.0001, fractionDigits: 4 }
      )

      addDockItem({
        id: `withdraw-${id}`,
        title: `Withdraw ${formattedAmount} ${params.tokenOut.symbol}`,
        icons: [
          <AssetComboIcon
            key="token"
            sizeClassName="size-7"
            {...params.tokenOut}
            chainName={withdrawDescription.tokenOutDeployment.chainName}
          />,
        ],
        explorerUrl: params.depositAddress
          ? buildIntentsExplorerTxUrl(params.depositAddress, {
              isConfidential: params.isConfidential,
            })
          : undefined,
        keyValueRows: [],
        renderContent: () => (
          <WithdrawStatus variant="dock" withdraw={withdraw} />
        ),
      })
    },
    [actorRef, addDockItem]
  )

  const dismissWithdraw = useCallback(
    (id: string) => {
      actorRef.send({ type: "DISMISS_WITHDRAW", id })
      removeDockItem(`withdraw-${id}`)
    },
    [actorRef, removeDockItem]
  )

  const markWithdrawSeen = useCallback(
    (id: string) => {
      actorRef.send({ type: "MARK_WITHDRAW_SEEN", id })
    },
    [actorRef]
  )

  const hasActiveWithdraw = useCallback(
    (id: string) => trackedWithdraws.some((w) => w.id === id),
    [trackedWithdraws]
  )

  const value = useMemo(
    () => ({
      trackedWithdraws,
      registerWithdraw,
      dismissWithdraw,
      markWithdrawSeen,
      hasActiveWithdraw,
    }),
    [
      trackedWithdraws,
      registerWithdraw,
      dismissWithdraw,
      markWithdrawSeen,
      hasActiveWithdraw,
    ]
  )

  return (
    <WithdrawTrackerMachineContext.Provider value={value}>
      {children}
    </WithdrawTrackerMachineContext.Provider>
  )
}

export function useWithdrawTrackerMachine() {
  const context = useContext(WithdrawTrackerMachineContext)

  if (!context) {
    throw new Error(
      "useWithdrawTrackerMachine must be used within a WithdrawTrackerMachineProvider"
    )
  }

  return context
}

export type { RegisterWithdrawParams } from "@src/machines/withdrawTrackerMachine"
